import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Box,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import axios from 'axios';

interface AddMovieModalProps {
  show: boolean;
  onHide: () => void;
  onAddMovie: (movie: any) => void;
  wishlistMode?: boolean;
}

const AddMovieModal = ({
  show,
  onHide,
  onAddMovie,
  wishlistMode = false,
}: AddMovieModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedMovie, setSelectedMovie] = useState<any | null>(null);
  const [type, setType] = useState('4K Ultra HD');
  const [isWishlist, setIsWishlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const imageBaseUrl = 'https://image.tmdb.org/t/p/';
  const imageSize = 'w185';

  /* Reset when modal opens */
  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedMovie(null);
      setType('4K Ultra HD');
      setIsWishlist(wishlistMode);
      setError(null);
    }
  }, [show, wishlistMode]);

  /* Debounced live search */
  useEffect(() => {
    const trimmed = searchTerm.trim();

    if (!trimmed) {
      setSearchResults([]);
      setError(null);
      return;
    }

    const handler = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          'https://api.themoviedb.org/3/search/movie',
          {
            params: {
              api_key: process.env.REACT_APP_TMDB_API_KEY,
              query: trimmed,
            },
          }
        );
        setSearchResults(response.data.results);
      } catch {
        setError('Failed to fetch movie data.');
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleAddMovie = () => {
    if (!selectedMovie) return;

    onAddMovie({
      title: selectedMovie.title,
      year: selectedMovie.release_date
        ? parseInt(selectedMovie.release_date.split('-')[0], 10)
        : 'N/A',
      type,
      image: selectedMovie.poster_path
        ? `${imageBaseUrl}${imageSize}${selectedMovie.poster_path}`
        : '',
      ...(isWishlist && { wishlist: true }),
    });

    onHide();
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle>{isWishlist ? 'Add to Wishlist' : 'Add Movie'}</DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          autoFocus
          label="Search for a movie"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {selectedMovie ? (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Selected Movie
            </Typography>

            <Box
              component="img"
              src={
                selectedMovie.poster_path
                  ? `${imageBaseUrl}${imageSize}${selectedMovie.poster_path}`
                  : ''
              }
              alt={selectedMovie.title}
              sx={{ width: 150, borderRadius: 1, mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>{isWishlist ? 'Wanted Format' : 'Type'}</InputLabel>
              <Select
                value={type}
                label={isWishlist ? 'Wanted Format' : 'Type'}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="Blu-Ray">Blu-Ray</MenuItem>
                <MenuItem value="4K Ultra HD">4K Ultra HD</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={isWishlist}
                  onChange={(e) => setIsWishlist(e.target.checked)}
                />
              }
              label="Add to Wishlist"
              sx={{ mt: 2 }}
            />
          </Box>
        ) : (
          searchResults.length > 0 && (
            <Grid
              container
              spacing={2}
              sx={{ maxHeight: 400, overflowY: 'auto', mt: 2 }}
            >
              {searchResults.map((movie) => (
                <Grid key={movie.id} size={{ xs: 6, sm: 4, md: 3 }}>
                  <Box
                    component="img"
                    src={
                      movie.poster_path
                        ? `${imageBaseUrl}${imageSize}${movie.poster_path}`
                        : ''
                    }
                    alt={movie.title}
                    onClick={() => setSelectedMovie(movie)}
                    sx={{
                      width: '100%',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                    }}
                  />
                  <Typography variant="body2" align="center">
                    {movie.title}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onHide}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAddMovie}
          disabled={!selectedMovie}
        >
          {isWishlist ? 'Add to Wishlist' : 'Add Movie'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMovieModal;